<template>
  <div :class="$style.wrapper">
    <component
      :is="DynamicTable" 
      :exportButton="true"
      :headers="headers"
      :rows="filteredRows"
      :loading-data="loadingData"
      :enable-pagination="true"
      :items-per-page="15"
    >
    <template #filters>
        <template :class="$style.filters">
          <div>
            <div :class="$style.filters__title">
              Filters
            </div>
            <div :class="$style.filters__section">
              
              <div :class="$style.inputGroup">
                    <label for="user">Users:</label>
                    <select v-model="selectedUser">
                      <option value="">All Users</option>
                      <option v-for="user in filterOptions.users" :key="user" :value="user">
                        {{ user }}
                      </option>
                    </select>
                  </div>
            </div>
          </div>
        </template>
      </template>
  </component>
  </div>
</template>

<script setup>
  import { ref, onMounted, computed, resolveDynamicComponent } from 'vue';
  import { useStore } from 'vuex';
  import BaseTable from '@components/common/BaseTable.vue';
  import MobileTable from '@components/common/MobileTable.vue';
  import { formatToUSD } from '@/helpers/currencyFormatter';
  import { formatDate } from '@/helpers/dateFormatter';
  import {
    CalendarDateRangeIcon,
    CurrencyDollarIcon,
    UserCircleIcon,
  } from '@heroicons/vue/24/outline';
import usersBalanceService from '@/services/usersBalanceService';

  const store = useStore();
  const originalRows = ref([]);
  const loadingData = ref(true);
  
  const isMobile = computed(() => store.getters['device/isMobile']);

  
  const DynamicTable = computed(() =>
    resolveDynamicComponent(isMobile.value ? MobileTable : BaseTable)
  );

  const headers = [
    { title: 'Name', icon: UserCircleIcon, key: 'name' },
    { title: 'Date', icon: CalendarDateRangeIcon, key: 'date' },
    { title: 'Balance User Start', icon: CurrencyDollarIcon, key: 'balanceStart' },
    { title: 'Gross Balance', icon: CurrencyDollarIcon, key: 'grossBalance' },
  ];

  const selectedUser = ref('');

  const filterOptions = computed(() => {
    const users = new Set();

    originalRows.value.forEach(row => {
      if (row.name) users.add(row.name);
    });

    return {
      users: [...users].sort(),
    };
  });

  const filteredRows = computed(() => {
    return originalRows.value.filter(row => {
      return (
        !selectedUser.value || row.name === selectedUser.value
      );
    });
  });


  const formatData = async (data) => {
    const formattedData = data.map(item => ({
      name: item.name_user,
      date: formatDate(item.date),
      balanceStart: formatToUSD(item.balance_user_start),
      grossBalance: formatToUSD(item.gross_balance),
    }));

    return formattedData;
  };

  const fetchData = async () => {
    try {
      loadingData.value = true
      const tableData = await usersBalanceService.getTableData();
      originalRows.value = await formatData(tableData.users_balance);
    } catch (error) {
      originalRows.value = [];
    } finally {
      loadingData.value = false;
    }
  };

  onMounted(async () => {
    await fetchData();
  });
</script>

<style module lang="scss">
  .wrapper {
    gap: 25px;
    display: flex;
    flex-wrap: wrap;
    margin-top: 20px;
    flex-direction: column;
  }

  .filters {
    gap: 10px;
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 15px;
    flex-direction: column;

    &__title {
      color: #D9D9D9;
      font-size: 16px;
      font-weight: 600;
      line-height: 36px;

      &Advanced {
        display: flex;
        margin: 10px 0;
        justify-content: flex-end;
      }
    }

    &__section {
      gap: 12px;
      display: flex;
    }

    @include respond-to(null, 'sm') {
      align-items: unset;

      &__section {
        flex-wrap: wrap;
      }
    }

    @include respond-to(null, 'xs') {
      &__section {
        flex-direction: column;

        .inputGroup {
            select,
            input {
              width: 100%;
            }
        }
      }
    }
  }

  .inputGroup {
    display: flex;
    font-size: 14px;
    flex-direction: column;

    label {
      color: #d9d9d9;
      margin-bottom: 4px;
    }

    select,
    input {
      padding: 8px;
      width: 150px;
      height: 45px;
      color: #fff;
      border-radius: 6px;
      border: 1px solid #535353;
      background: rgba(217, 217, 217, 0.05);
    }

    select {
      option {
        color: black;
      }
    }
  }
</style>